import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    Tabs,
    Tab,
    LinearProgress,
    Chip
} from '@mui/material';
import { 
    Download as DownloadIcon, 
    Folder as FolderIcon, 
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayArrowIcon,
    Pause as PauseIcon,
    Check as CheckIcon,
    Error as ErrorIcon,
    HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api.service';

interface ReportResponse {
    success: boolean;
    message?: string;
    filename?: string;
    jobId?: string;
}

interface ReportFile {
    filename: string;
    createdAt: string;
    size: number;
}

interface JobStatus {
    id: string;
    state: 'active' | 'waiting' | 'delayed' | 'completed' | 'failed';
    progress: number;
    result?: { filename?: string };
    failReason?: string;
    timestamp: number;
    processedOn?: number;
    finishedOn?: number;
    data: {
        reportType: string;
        userId: string;
        timestamp: string;
    };
}

const Reports: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [filename, setFilename] = useState<string | null>(null);
    const [jobId, setJobId] = useState<string | null>(null);
    
    // Reports list modal state
    const [reportsModalOpen, setReportsModalOpen] = useState<boolean>(false);
    const [reports, setReports] = useState<ReportFile[]>([]);
    const [reportsLoading, setReportsLoading] = useState<boolean>(false);
    const [reportsError, setReportsError] = useState<string | null>(null);
    
    // Jobs list state
    const [jobs, setJobs] = useState<JobStatus[]>([]);
    const [jobsLoading, setJobsLoading] = useState<boolean>(false);
    const [jobsError, setJobsError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<number>(0);
    
    // Poll for job status
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
    
    // Fetch reports list
    const fetchReports = async () => {
        setReportsLoading(true);
        setReportsError(null);
        
        try {
            const response = await apiService.getReportsList();
            if (response.data.success) {
                setReports(response.data.data);
            } else {
                throw new Error('Failed to fetch reports');
            }
        } catch (err: any) {
            setReportsError(err.message || 'Failed to fetch reports');
            setReports([]);
        } finally {
            setReportsLoading(false);
        }
    };
    
    // Fetch jobs list
    const fetchJobs = useCallback(async () => {
        setJobsLoading(true);
        setJobsError(null);
        
        try {
            const response = await apiService.getReportJobs();
            if (response.data.success) {
                setJobs(response.data.data);
                
                // Check if we have an active job
                const activeJob = response.data.data.find(job => 
                    job.state === 'active' || job.state === 'waiting' || job.state === 'delayed'
                );
                
                // If we have an active job and we're not already polling, start polling
                if (activeJob && !pollingInterval) {
                    startPolling();
                }
                
                // If we don't have an active job and we are polling, stop polling
                if (!activeJob && pollingInterval) {
                    stopPolling();
                }
                
                // If we have a completed job that matches our jobId, update the UI
                if (jobId) {
                    const ourJob = response.data.data.find(job => job.id === jobId);
                    if (ourJob && ourJob.state === 'completed' && ourJob.result?.filename) {
                        setSuccess(true);
                        setFilename(ourJob.result.filename);
                        setLoading(false);
                        setJobId(null); // Clear job ID to stop checking
                    } else if (ourJob && ourJob.state === 'failed') {
                        setError(ourJob.failReason || 'Report generation failed');
                        setLoading(false);
                        setJobId(null); // Clear job ID to stop checking
                    }
                }
            } else {
                throw new Error('Failed to fetch jobs');
            }
        } catch (err: any) {
            setJobsError(err.message || 'Failed to fetch jobs');
            setJobs([]);
        } finally {
            setJobsLoading(false);
        }
    }, [jobId, pollingInterval]);
    
    // Start polling for job status
    const startPolling = useCallback(() => {
        if (!pollingInterval) {
            const interval = setInterval(() => {
                fetchJobs();
            }, 5000); // Poll every 5 seconds
            setPollingInterval(interval);
        }
    }, [fetchJobs, pollingInterval]);
    
    // Stop polling for job status
    const stopPolling = useCallback(() => {
        if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
        }
    }, [pollingInterval]);
    
    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [pollingInterval]);
    
    // Handle opening the reports modal
    const handleOpenReportsModal = () => {
        setReportsModalOpen(true);
        fetchReports();
        fetchJobs();
    };
    
    // Handle downloading a report
    const handleDownloadReport = (filename: string) => {
        apiService.downloadReport(filename);
    };
    
    // Handle deleting a report
    const handleDeleteReport = async (filename: string) => {
        if (window.confirm(`Are you sure you want to delete the report "${filename}"?`)) {
            try {
                const response = await apiService.deleteReport(filename);
                if (response.data.success) {
                    // Refresh the reports list
                    fetchReports();
                } else {
                    throw new Error(response.data.message || 'Failed to delete report');
                }
            } catch (err: any) {
                setReportsError(err.message || 'Failed to delete report');
            }
        }
    };
    
    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        if (newValue === 0) {
            fetchReports();
        } else {
            fetchJobs();
        }
    };

    // Format file size to human-readable format
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    // Format date to human-readable format
    const formatDate = (dateString: string | number): string => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };
    
    // Get status chip for job
    const getStatusChip = (status: string) => {
        switch (status) {
            case 'active':
                return <Chip icon={<PlayArrowIcon />} label="Active" color="primary" size="small" />;
            case 'waiting':
                return <Chip icon={<PendingIcon />} label="Waiting" color="secondary" size="small" />;
            case 'delayed':
                return <Chip icon={<PauseIcon />} label="Delayed" color="warning" size="small" />;
            case 'completed':
                return <Chip icon={<CheckIcon />} label="Completed" color="success" size="small" />;
            case 'failed':
                return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };
    
    const generateReport = async () => {
        setLoading(true);
        setError(null);
        setSuccess(false);
        setFilename(null);
        setJobId(null);
        
        try {
            const response = await apiService.get<ReportResponse>('/reports/sets-breakdown-by-ratio');
            
                if (response.data.success) {
                    if (response.data.jobId) {
                        // Background job started
                        setJobId(response.data.jobId);
                        startPolling();
                        
                        // Open the reports modal and switch to the Jobs tab
                        setReportsModalOpen(true);
                        setActiveTab(1); // Switch to Jobs tab
                        fetchJobs(); // Fetch jobs immediately
                    } else if (response.data.filename) {
                        // Immediate response with filename
                        setSuccess(true);
                        setFilename(response.data.filename);
                        setLoading(false);
                    }
            } else {
                throw new Error(response.data.message || 'Failed to generate report');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate report');
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Reports
            </Typography>
            
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Sets Breakdown by Ratio
                </Typography>
                
                <Typography variant="body1" paragraph>
                    This report generates a CSV file with a breakdown of all sets by their part-out value and ratio metrics.
                    The report includes the following information:
                </Typography>
                
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                    <Box component="li">Set number and name</Box>
                    <Box component="li">UPC, Release Date, Retirement Date, MSRP</Box>
                    <Box component="li">Total part-out value</Box>
                    <Box component="li">Part-out value and percentage at different ratio thresholds (1:1, 3:1, 5:1, 10:1)</Box>
                    <Box component="li">Minifigure part-out value and percentage at different ratio thresholds</Box>
                </Box>
                
                <Box mt={3}>
                    <Button 
                        variant="contained" 
                        onClick={generateReport}
                        disabled={loading || !!jobId}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Generating Report...' : 'Generate Report'}
                    </Button>
                </Box>
                
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {jobId && !success && !error && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            Report generation has been queued (Job ID: {jobId})
                        </Typography>
                        <Typography variant="body2">
                            This may take several minutes. You can check the status in the "Jobs" tab of the reports modal.
                        </Typography>
                        <Button 
                            variant="text" 
                            color="primary" 
                            size="small"
                            onClick={handleOpenReportsModal}
                            sx={{ mt: 1 }}
                        >
                            View Jobs
                        </Button>
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Report generated successfully!
                        {filename && (
                            <Box mt={1}>
                                <Typography variant="body2">
                                    The report has been saved as {filename}
                                </Typography>
                                <Button 
                                    variant="text" 
                                    color="primary" 
                                    size="small"
                                    onClick={() => handleDownloadReport(filename)}
                                    startIcon={<DownloadIcon />}
                                    sx={{ mt: 1 }}
                                >
                                    Download Report
                                </Button>
                            </Box>
                        )}
                    </Alert>
                )}
                
                <Box mt={4} display="flex" justifyContent="center">
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleOpenReportsModal}
                        startIcon={<FolderIcon />}
                    >
                        View Reports & Jobs
                    </Button>
                </Box>
                
                {/* Reports & Jobs Modal */}
                <Dialog
                    open={reportsModalOpen}
                    onClose={() => setReportsModalOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Reports Management</DialogTitle>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={handleTabChange} aria-label="reports tabs">
                            <Tab label="Generated Reports" />
                            <Tab label="Jobs" />
                        </Tabs>
                    </Box>
                    <DialogContent>
                        {/* Reports Tab */}
                        {activeTab === 0 && (
                            <>
                                {reportsLoading ? (
                                    <Box display="flex" justifyContent="center" p={3}>
                                        <CircularProgress />
                                    </Box>
                                ) : reportsError ? (
                                    <Alert severity="error">{reportsError}</Alert>
                                ) : reports.length === 0 ? (
                                    <Alert severity="info">No reports found</Alert>
                                ) : (
                                    <List>
                                        {reports.map((report, index) => (
                                            <React.Fragment key={report.filename}>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={report.filename}
                                                        secondary={`Created: ${formatDate(report.createdAt)} | Size: ${formatFileSize(report.size)}`}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton 
                                                            edge="end" 
                                                            onClick={() => handleDownloadReport(report.filename)}
                                                            title="Download Report"
                                                            sx={{ mr: 1 }}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                        <IconButton 
                                                            edge="end" 
                                                            onClick={() => handleDeleteReport(report.filename)}
                                                            title="Delete Report"
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                {index < reports.length - 1 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                )}
                            </>
                        )}
                        
                        {/* Jobs Tab */}
                        {activeTab === 1 && (
                            <>
                                <Box display="flex" justifyContent="flex-end" mb={2}>
                                    <Button
                                        startIcon={<RefreshIcon />}
                                        onClick={fetchJobs}
                                        disabled={jobsLoading}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                                
                                {jobsLoading ? (
                                    <Box display="flex" justifyContent="center" p={3}>
                                        <CircularProgress />
                                    </Box>
                                ) : jobsError ? (
                                    <Alert severity="error">{jobsError}</Alert>
                                ) : jobs.length === 0 ? (
                                    <Alert severity="info">No jobs found</Alert>
                                ) : (
                                    <List>
                                        {jobs.map((job, index) => (
                                            <React.Fragment key={job.id}>
                                                <ListItem>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center">
                                                                <Typography variant="body1" sx={{ mr: 1 }}>
                                                                    {job.data.reportType}
                                                                </Typography>
                                                                {getStatusChip(job.state)}
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <>
                                                                <Typography variant="body2">
                                                                    Job ID: {job.id}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    Created: {formatDate(job.timestamp)}
                                                                    {job.processedOn && ` | Started: ${formatDate(job.processedOn)}`}
                                                                    {job.finishedOn && ` | Finished: ${formatDate(job.finishedOn)}`}
                                                                </Typography>
                                                                {job.state === 'active' && (
                                                                    <Box sx={{ width: '100%', mt: 1 }}>
                                                                        <LinearProgress 
                                                                            variant="determinate" 
                                                                            value={job.progress} 
                                                                        />
                                                                        <Typography variant="body2" align="center">
                                                                            {Math.round(job.progress)}%
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                {job.state === 'completed' && job.result?.filename && (
                                                                    <Box mt={1}>
                                                                        <Button 
                                                                            variant="text" 
                                                                            color="primary" 
                                                                            size="small"
                                                                            onClick={() => job.result?.filename && handleDownloadReport(job.result.filename)}
                                                                            startIcon={<DownloadIcon />}
                                                                        >
                                                                            Download {job.result?.filename}
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                                {job.state === 'failed' && job.failReason && (
                                                                    <Typography variant="body2" color="error">
                                                                        Error: {job.failReason}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        }
                                                    />
                                                </ListItem>
                                                {index < jobs.length - 1 && <Divider />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setReportsModalOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </Container>
    );
};

export default Reports;
